import React, { useState } from 'react';

const HomePage = ({ setCurrentPage }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Chronaxis</h1>
              <span className="ml-3 text-sm text-gray-600 hidden sm:block">Gestion d'Emplois du Temps</span>
            </div>
            
            {/* Desktop Menu */}
            <nav className="hidden md:flex gap-2">
              <button onClick={() => setCurrentPage('admin')} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">Admin</button>
              <button onClick={() => setCurrentPage('student')} className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100">Étudiant</button>
              <button onClick={() => setCurrentPage('teacher')} className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100">Enseignant</button>
              <button onClick={() => setCurrentPage('contact')} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Contact</button>
            </nav>
            
            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="flex flex-col space-y-2">
                <button onClick={() => { setCurrentPage('admin'); setMobileMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">Admin</button>
                <button onClick={() => { setCurrentPage('student'); setMobileMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100">Étudiant</button>
                <button onClick={() => { setCurrentPage('teacher'); setMobileMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100">Enseignant</button>
                <button onClick={() => { setCurrentPage('contact'); setMobileMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Contact</button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Simplifiez la gestion de vos emplois du temps
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Solution complète pour la gestion des emplois du temps universitaires. 
                Créez, modifiez et consultez les plannings en toute simplicité.
              </p>
              <div className="flex gap-4">
                <button 
                  onClick={() => setCurrentPage('admin')} 
                  className="px-6 py-3 text-white bg-primary-600 rounded-lg hover:bg-primary-700 font-medium"
                >
                  Commencer
                </button>
                <button 
                  onClick={() => setCurrentPage('student')} 
                  className="px-6 py-3 text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 font-medium"
                >
                  Consulter un emploi
                </button>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="text-center mb-4">
                <h3 className="font-semibold text-gray-900">Emploi du Temps - Info1-1</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-2 bg-gray-100 rounded text-center font-medium">Lundi</div>
                <div className="p-2 bg-gray-100 rounded text-center font-medium">Mardi</div>
                <div className="p-2 bg-gray-100 rounded text-center font-medium">Mercredi</div>
                <div className="p-2 bg-blue-100 text-blue-800 rounded text-center">Programmation</div>
                <div className="p-2 bg-green-100 text-green-800 rounded text-center">Base de données</div>
                <div className="p-2 bg-purple-100 text-purple-800 rounded text-center">Réseaux</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-12">Fonctionnalités principales</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '📅', title: 'Gestion complète', desc: 'Créez et gérez facilement les emplois du temps' },
              { icon: '👥', title: 'Multi-utilisateurs', desc: 'Interface admin et consultation étudiante' },
              { icon: '🔄', title: 'Temps réel', desc: 'Modifications instantanées et synchronisation' },
              { icon: '📱', title: 'Responsive', desc: 'Accessible sur tous les appareils' },
              { icon: '📄', title: 'Export PDF', desc: 'Génération automatique de PDFs' },
              { icon: '⚡', title: 'Drag & Drop', desc: 'Interface intuitive avec glisser-déposer' }
            ].map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-lg bg-gray-50">
                <div className="text-3xl mb-4">{feature.icon}</div>
                <h4 className="font-semibold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-12">Pourquoi choisir Chronaxis ?</h3>
          <div className="space-y-6">
            {[
              { title: 'Simplicité d\'utilisation', desc: 'Interface intuitive conçue pour les établissements universitaires' },
              { title: 'Gestion des conflits', desc: 'Détection automatique des conflits de salles, enseignants et créneaux' },
              { title: 'Flexibilité', desc: 'Adaptation à tous types d\'organisations : cours, TD, TP par groupes' },
              { title: 'Accès libre étudiants', desc: 'Les étudiants peuvent consulter leurs emplois sans authentification' }
            ].map((benefit, index) => (
              <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-lg">
                <span className="text-green-600 text-xl">✅</span>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{benefit.title}</h4>
                  <p className="text-sm text-gray-600">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-semibold mb-4">Chronaxis</h4>
              <p className="text-gray-400 text-sm">Solution de gestion d'emplois du temps pour l'enseignement supérieur</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Liens rapides</h4>
              <div className="space-y-2">
                <button onClick={() => setCurrentPage('admin')} className="block text-gray-400 hover:text-white text-sm">Espace Admin</button>
                <button onClick={() => setCurrentPage('student')} className="block text-gray-400 hover:text-white text-sm">Espace Étudiant</button>
                <button onClick={() => setCurrentPage('teacher')} className="block text-gray-400 hover:text-white text-sm">Espace Enseignant</button>
                <button onClick={() => setCurrentPage('contact')} className="block text-gray-400 hover:text-white text-sm">Contact</button>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">&copy; 2024 Chronaxis. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;